/**
 * Creación de un trazo de tinta a partir de puntos sueltos.
 * Lo comparten la herramienta de pluma del navegador y el puente nativo del iPad,
 * para que un trazo hecho con Apple Pencil quede idéntico a uno hecho con el ratón.
 */
import type { DrawShape } from '../../shared/types.ts'
import { simplifyInk, streamlinePoints, thinPoints, type InkPoint } from '../lib/freehand.ts'
import { createDraw } from '../state/shapes.ts'
import { recognizeShape } from './beautify.ts'
import type { AppContext } from './types.ts'

export interface StrokeStyle {
  color: string
  size: number
  highlighter: boolean
  /** 0 = grosor parejo; 0.55 = respuesta normal a la presión */
  thinning?: number
}

/**
 * Suaviza, simplifica y guarda el trazo en el tablero.
 * Devuelve la figura creada, o null si el trazo venía vacío.
 */
export function commitStroke(
  ctx: AppContext,
  rawPoints: InkPoint[],
  style: StrokeStyle,
  options: { alreadySmoothed?: boolean } = {},
): DrawShape | null {
  if (rawPoints.length === 0) return null

  const smoothed = options.alreadySmoothed ? rawPoints : streamlinePoints(rawPoints, 0.5)
  // La tolerancia es de 0.15 px EN PANTALLA: con una fija en unidades del
  // lienzo, al escribir con zoom el trazo guardado se apartaba del que veías
  // (a 400 % eran 1.6 px) y "saltaba" al soltar el lápiz. Medido sobre
  // escritura realista: con 0.3 px el trazo cambiaba 1.3 px al soltar; con
  // 0.15 px, menos de 0.3 px, a cambio de ~2x puntos guardados.
  const zoom = Math.max(ctx.store.camera.z, 0.1)
  const thinned = style.highlighter ? smoothed : thinPoints(smoothed, style.size)
  const simplified = simplifyInk(thinned, Math.min(0.25, 0.15 / zoom))
  const points = simplified.length >= 2 ? simplified : thinned

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const point of points) {
    minX = Math.min(minX, point.x)
    maxX = Math.max(maxX, point.x)
    minY = Math.min(minY, point.y)
    maxY = Math.max(maxY, point.y)
  }
  const w = Math.max(maxX - minX, 0.01)
  const h = Math.max(maxY - minY, 0.01)

  const flat: number[] = []
  for (const point of points) flat.push(point.x - minX, point.y - minY, point.p)

  const shape = createDraw({
    points: flat,
    x: minX,
    y: minY,
    w,
    h,
    color: style.color,
    size: style.size,
    highlighter: style.highlighter,
    createdBy: ctx.userId,
    z: ctx.board.maxZ() + 1,
  })
  if (!style.highlighter && style.thinning !== undefined) shape.thinning = style.thinning

  // "Mejorar formas": si el trazo se parece a una figura conocida, se sustituye.
  if (!style.highlighter && ctx.store.enhanceShapes) {
    const geo = recognizeShape(points, {
      color: style.color,
      strokeWidth: Math.max(2, style.size * 0.8),
      createdBy: ctx.userId,
      z: shape.z,
    })
    if (geo) {
      ctx.board.put(geo)
      ctx.touch()
      return null
    }
  }

  ctx.board.put(shape)
  ctx.touch()
  return shape
}
